import {
  OAUTH_STATE_KEYS,
  OAUTH_ERRORS,
  safeBase64Encode,
  safeBase64Decode,
} from './oauth-constants';

export interface RedirectInfo {
  [OAUTH_STATE_KEYS.WL_ID]?: string;
  [OAUTH_STATE_KEYS.SURVEY_TOKEN]?: string;
  [OAUTH_STATE_KEYS.ORIGIN]?: string;
  [OAUTH_STATE_KEYS.ALLOWED_GROUPS]?: string;
  [OAUTH_STATE_KEYS.STRIPE_DEEP_LINK]?: string;
}

export interface OAuthStateData {
  oauthState: string | null;
  redirectInfo: RedirectInfo;
  isLegacyFormat: boolean;
}

export interface CompositeOAuthState {
  oauthState: string;
  redirectInfo: RedirectInfo;
}

/**
 * Detects if the state parameter is in legacy key=value format or new JSON format
 * KPN Backward Compatibility: Only treat as legacy if it contains known application keys
 */
export const isLegacyStateFormat = (decodedState: string): boolean => {
  if (!decodedState || typeof decodedState !== 'string') {
    return true;
  }

  try {
    const parsed = JSON.parse(decodedState);
    return !(
      parsed.oauthState !== undefined && parsed.redirectInfo !== undefined
    );
  } catch {
    // If JSON parsing fails, check if it looks like our specific legacy format
    // Only treat as legacy if it contains key=value pairs with known application keys
    const hasLegacyKeys = Object.values(OAUTH_STATE_KEYS).some((key) =>
      decodedState.includes(`${key}=`)
    );
    return hasLegacyKeys && decodedState.includes('&');
  }
};

/**
 * Creates a new composite OAuth state with original OAuth state and redirect info
 */
export const encodeOAuthState = (
  originalState: string | null,
  redirectInfo: RedirectInfo
): string => {
  try {
    const compositeState: CompositeOAuthState = {
      oauthState: originalState || '',
      redirectInfo,
    };

    const jsonString = JSON.stringify(compositeState);
    const encoded = safeBase64Encode(jsonString);

    if (!encoded) {
      console.warn('[OAuth] Failed to encode state, using fallback');
      return originalState || '';
    }

    return encoded;
  } catch (error) {
    console.error('[OAuth] Error encoding OAuth state:', error);
    return originalState || '';
  }
};

/**
 * Safely extracts value from key=value pair
 */
const extractLegacyValue = (
  items: string[],
  key: string
): string | undefined => {
  try {
    return items
      ?.find((item) => item.startsWith(`${key}=`))
      ?.slice(`${key}=`.length)
      ?.trim();
  } catch (error) {
    console.warn(`[OAuth] Failed to extract ${key}:`, error);
    return undefined;
  }
};

/**
 * Parses OAuth state parameter handling both legacy and new formats
 * KPN Backward Compatibility: Handles legacy key=value&key=value state format
 */
export const decodeOAuthState = (state: string): OAuthStateData => {
  if (!state || typeof state !== 'string') {
    return {
      oauthState: null,
      redirectInfo: {},
      isLegacyFormat: true,
    };
  }

  const decodedState = safeBase64Decode(state);
  if (!decodedState) {
    return {
      oauthState: null,
      redirectInfo: {},
      isLegacyFormat: true,
    };
  }

  // Try parsing as new JSON format first
  if (!isLegacyStateFormat(decodedState)) {
    try {
      const parsed: CompositeOAuthState = JSON.parse(decodedState);
      return {
        oauthState: parsed.oauthState,
        redirectInfo: parsed.redirectInfo,
        isLegacyFormat: false,
      };
    } catch (error) {
      console.warn(
        '[OAuth] JSON parsing failed, falling back to legacy format:',
        error
      );
    }
  }

  // Legacy format: key=value&key=value
  const splitted = decodedState?.split('&') || [];

  return {
    oauthState: null, // No original OAuth state in legacy format
    redirectInfo: {
      [OAUTH_STATE_KEYS.WL_ID]: extractLegacyValue(
        splitted,
        OAUTH_STATE_KEYS.WL_ID
      ),
      [OAUTH_STATE_KEYS.SURVEY_TOKEN]: extractLegacyValue(
        splitted,
        OAUTH_STATE_KEYS.SURVEY_TOKEN
      ),
      [OAUTH_STATE_KEYS.ORIGIN]: extractLegacyValue(
        splitted,
        OAUTH_STATE_KEYS.ORIGIN
      ),
      [OAUTH_STATE_KEYS.ALLOWED_GROUPS]: extractLegacyValue(
        splitted,
        OAUTH_STATE_KEYS.ALLOWED_GROUPS
      ),
      [OAUTH_STATE_KEYS.STRIPE_DEEP_LINK]: extractLegacyValue(
        splitted,
        OAUTH_STATE_KEYS.STRIPE_DEEP_LINK
      ),
    },
    isLegacyFormat: true,
  };
};

/**
 * Safely extracts waiting list ID from pathname
 */
const extractWaitingListId = (pathname?: string): string | undefined => {
  try {
    if (!pathname) return undefined;
    const pathParts = pathname.split('/');
    return pathParts.length > 3 ? pathParts[3] : undefined;
  } catch (error) {
    console.warn('[OAuth] Failed to extract waiting list ID:', error);
    return undefined;
  }
};

/**
 * Safely extracts survey token from search params
 */
const extractSurveyToken = (search?: string): string | undefined => {
  try {
    if (!search) return undefined;
    const tokenMatch = search.match(/token=([^&]*)/);
    return tokenMatch ? tokenMatch[1] : undefined;
  } catch (error) {
    console.warn('[OAuth] Failed to extract survey token:', error);
    return undefined;
  }
};

/**
 * Creates redirect info from location state for OAuth flows
 */
export const createRedirectInfoFromLocation = (
  locationState: any,
  LOCATION_STATES: any,
  routes: any
): RedirectInfo => {
  try {
    if (!locationState || !LOCATION_STATES || !routes) {
      return {};
    }

    const redirectPath = locationState[LOCATION_STATES.REDIRECT_PATH];
    if (!redirectPath) {
      return {};
    }

    const isSurveyAfterEvent = redirectPath.pathname?.includes(
      routes.surveyAfterEvent
    );
    const wlId = extractWaitingListId(redirectPath.pathname);
    const surveyAfterEventToken = isSurveyAfterEvent
      ? extractSurveyToken(redirectPath.search)
      : undefined;

    const result: RedirectInfo = {};

    if (wlId) {
      result[OAUTH_STATE_KEYS.WL_ID] = wlId;
    }

    if (surveyAfterEventToken) {
      result[OAUTH_STATE_KEYS.SURVEY_TOKEN] = surveyAfterEventToken;
    }

    return result;
  } catch (error) {
    console.error('[OAuth] Error creating redirect info from location:', error);
    return {};
  }
};
